/**
 * Support Platform Service — Super Admin cross-org platform-lane queue
 * (`/platform/support-requests/*`). Mirrors supportAdminService's shape so
 * both can drive the same queue UI (SupportQueueShell) via config, per the
 * plan's "same layout parameterized by service fns."
 */

import apiClient from './apiClient';
import { downloadSupportAttachment } from './supportAttachmentDownload';
import type {
  SupportRequestDetail,
  SupportRequestStatus,
  SupportRequestType,
  SupportReplyInput,
  UpdateSupportRequestInput,
  SupportAssignableHandler,
  SupportRequesterOption,
} from '@upskill/shared';
import type { PaginatedSupportRequests } from './supportAdminService';

export type { PaginatedSupportRequests } from './supportAdminService';

const BASE = '/api/v1/platform/support-requests';

const bgConfig = { _backgroundRequest: true } as any;

export interface ListPlatformSupportParams {
  page?: number;
  limit?: number;
  status?: SupportRequestStatus;
  requesterId?: string;
  type?: SupportRequestType;
  organizationId?: string;
}

interface PaginatedEnvelope extends PaginatedSupportRequests {
  success: boolean;
}

interface UnreadCountEnvelope {
  success: boolean;
  data: { count: number };
}

interface DetailEnvelope {
  success: boolean;
  data: SupportRequestDetail;
}

export const listRequests = async (
  params: ListPlatformSupportParams = {},
): Promise<PaginatedSupportRequests> => {
  const response = await apiClient.get<PaginatedEnvelope>(BASE, { params });
  const { success: _success, ...paginated } = response.data;
  return paginated;
};

export const getUnreadCount = async (): Promise<number> => {
  const response = await apiClient.get<UnreadCountEnvelope>(`${BASE}/unread-count`, bgConfig);
  return response.data.data.count;
};

export const getRequestDetail = async (id: string): Promise<SupportRequestDetail> => {
  const response = await apiClient.get<DetailEnvelope>(`${BASE}/${id}`);
  return response.data.data;
};

export const replyToRequest = async (
  id: string,
  input: SupportReplyInput,
): Promise<SupportRequestDetail> => {
  const response = await apiClient.post<DetailEnvelope>(`${BASE}/${id}/messages`, input);
  return response.data.data;
};

export const updateRequest = async (
  id: string,
  input: UpdateSupportRequestInput,
): Promise<SupportRequestDetail> => {
  const response = await apiClient.patch<DetailEnvelope>(`${BASE}/${id}`, input);
  return response.data.data;
};

/** PLATFORM -> ACCOUNT: hands the thread back to the requester's Org Admins. */
export const bounceRequest = async (id: string): Promise<SupportRequestDetail> => {
  const response = await apiClient.post<DetailEnvelope>(`${BASE}/${id}/bounce`);
  return response.data.data;
};

export const downloadAttachment = downloadSupportAttachment;

/** Handlers eligible to be reassigned a platform-lane request (super admins). */
export const listAssignableHandlers = async (): Promise<SupportAssignableHandler[]> => {
  const response = await apiClient.get<{ success: boolean; data: SupportAssignableHandler[] }>(
    `${BASE}/assignable-handlers`,
  );
  return response.data.data;
};

/** Distinct requesters across every org in the platform queue — feeds both the
 * requester filter and (by deriving distinct organizationId/Name pairs) the org filter. */
export const listRequesters = async (): Promise<SupportRequesterOption[]> => {
  const response = await apiClient.get<{ success: boolean; data: SupportRequesterOption[] }>(
    `${BASE}/requesters`,
  );
  return response.data.data;
};

const supportPlatformService = {
  listRequests,
  getUnreadCount,
  getRequestDetail,
  replyToRequest,
  updateRequest,
  bounceRequest,
  downloadAttachment,
  listAssignableHandlers,
  listRequesters,
};

export default supportPlatformService;
